import React, { useMemo, useState } from "react";

import { Briefcase, Users } from "lucide-react";

import { ContactRolesReferenceCard } from "./contact-roles-reference-card";
import { DepartedRowSection } from "./DepartedRowSection";
import {
  clampLevel,
  findPairForMatrixCell,
  resolvePairDisplayPosture,
  formatPostureLabel,
  formatLevel,
  getCoverageMatrixDotColor,
  getInitials,
  getPostureLetter,
  getPairGoalTargetRl,
  formatGoalHorizonLabel,
  buildMeddGroupHeaderSpans,
  buildPerContactMatrixColumns,
  formatMeddGroupLabelDisplay,
  formatMeddKeyDisplay,
  formatMeddRoleLabelDisplay,
  MAPPED_UNASSESSED_DOT_COLOR,
  resolveCustomerMemberTitle,
  splitStakeholdersForMatrix,
} from "./relationshipUtils";
import {
  VendorRowLabel,
  ExecTeamPsLegend,
  MATRIX_VENDOR_STAKEHOLDER_COL_WIDTH,
} from "./vendor-team-matrix-ui";
import { getCustomerContactAvatarBg } from "../../../Utils/Helper/profileAvatarUi";

function CellSavedToast() {
  return (
    <span className="mcell-saved-toast" role="status">
      Assessment saved
    </span>
  );
}

const GHOST_CELL = (
  <td className="mcol-ghost" style={{ pointerEvents: "none" }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        opacity: 0.3,
      }}
    >
      <span style={{ color: "var(--gray-300)", fontSize: 10 }}>—</span>
    </div>
  </td>
);

function MatrixCell({
  stakeholder,
  columnDef,
  pair,
  activeContact,
  cellMode,
  onAssess,
  onUnmappedCell,
  isHighlighted,
  showSavedToast,
  cellKey = null,
  coverageColumn = false,
}) {
  const contact = activeContact || columnDef?.contact;

  const tdClass = coverageColumn ? "mx-hl" : undefined;

  if (!contact || stakeholder.empty) {
    return (
      <td className={coverageColumn ? "mcol-ghost mx-hl" : "mcol-ghost"}>
        <div className="mcell unassessed" style={{ minHeight: 42 }}>
          <span style={{ fontSize: 10, color: "var(--gray-300)" }}>—</span>
        </div>
      </td>
    );
  }

  const mapped = Boolean(pair);
  const assessed =
    mapped && pair.rlLevel !== null && pair.rlLevel !== undefined;
  const assessPayload = { pair, stakeholder, contact };
  const unmappedPayload = { stakeholder, contact };
  const highlightCls = isHighlighted ? " mcell-highlighted" : "";
  const hoverTitle = isHighlighted ? "Recently assessed" : null;
  const cellDataAttrs = {
    ...(cellKey ? { "data-matrix-cell-key": cellKey } : {}),
    ...(contact?.id ? { "data-cust": String(contact.id) } : {}),
  };

  if (!mapped) {
    return (
      <td className={tdClass} style={{ position: "relative" }}>
        <button
          type="button"
          className={`mcell unassessed${highlightCls}`}
          style={{
            border: "none",
            background: "transparent",
            width: "100%",
            cursor: "pointer",
          }}
          title={hoverTitle || "Not mapped — click to map this relationship"}
          onClick={() => onUnmappedCell?.(unmappedPayload)}
          {...cellDataAttrs}
        >
          <div className="dot-wrap">
            <div className="cell-empty-dot" />
          </div>
        </button>
        {showSavedToast ? <CellSavedToast /> : null}
      </td>
    );
  }

  if (cellMode === "posture") {
    const posture =
      mapped && pair
        ? resolvePairDisplayPosture(pair, contact)
        : formatPostureLabel(contact?.posture);
    const letter = getPostureLetter(posture);
    const isUnknown = String(posture).toLowerCase() === "unknown";
    const pendingCls = pair?.pendingConfirm ? " mcell-pending-review" : "";
    return (
      <td className={tdClass} style={{ position: "relative" }}>
        <button
          type="button"
          className={`mcell assessed${highlightCls}${pendingCls}`}
          style={{
            background: "transparent",
            border: "none",
            width: "100%",
            cursor: "pointer",
          }}
          title={hoverTitle || undefined}
          onClick={() => onAssess(assessPayload)}
          {...cellDataAttrs}
        >
          <span
            className={`pcell-badge${isUnknown ? " pu" : ""}`}
            title={posture}
          >
            {letter}
          </span>
          {pair?.pendingConfirm ? (
            <span
              className="mcell-review-badge"
              title="Inherited RL — review pending"
            >
              Review
            </span>
          ) : null}
        </button>
        {showSavedToast ? <CellSavedToast /> : null}
      </td>
    );
  }

  if (cellMode === "goals") {
    const goalTarget = assessed ? getPairGoalTargetRl(pair) : null;
    const rl = assessed ? clampLevel(pair.rlLevel) : null;
    const dotColor =
      rl !== null ? getCoverageMatrixDotColor(rl) : MAPPED_UNASSESSED_DOT_COLOR;
    const pendingCls = pair?.pendingConfirm ? " mcell-pending-review" : "";
    return (
      <td className={tdClass} style={{ position: "relative" }}>
        <button
          type="button"
          className={`mcell assessed${goalTarget ? " goal-cell" : ""}${highlightCls}${pendingCls}`}
          style={{
            background: goalTarget ? "var(--gray-50)" : "transparent",
            border: "none",
            width: "100%",
            cursor: "pointer",
            ...(goalTarget ? {} : { padding: 6 }),
          }}
          title={hoverTitle || undefined}
          onClick={() => onAssess(assessPayload)}
          {...cellDataAttrs}
        >
          {goalTarget ? (
            <>
              <div
                className="rl-dot"
                style={{ background: dotColor }}
                aria-hidden
              />
              <div className="goal-label">
                RL {rl}
                <span className="goal-target">
                  <span className="goal-arrow">→</span>
                  {goalTarget}
                </span>
              </div>
              {pair.goalTargetHorizon ? (
                <div className="goal-horizon">
                  {formatGoalHorizonLabel(pair.goalTargetHorizon)}
                </div>
              ) : null}
            </>
          ) : (
            <div
              style={{
                fontSize: 10,
                color: "var(--gray-700)",
                fontWeight: 600,
              }}
            >
              No goal set
            </div>
          )}
          {pair?.pendingConfirm ? (
            <span
              className="mcell-review-badge"
              title="Inherited RL — review pending"
            >
              Review
            </span>
          ) : null}
        </button>
        {showSavedToast ? <CellSavedToast /> : null}
      </td>
    );
  }

  if (assessed) {
    const rl = clampLevel(pair.rlLevel);
    const goalTarget = getPairGoalTargetRl(pair);
    const dotColor = getCoverageMatrixDotColor(rl);
    const goalRingCls = goalTarget ? " dot-goal-ring on-track" : "";
    const pendingCls = pair.pendingConfirm ? " mcell-pending-review" : "";
    const title = goalTarget
      ? `${formatLevel("RL", rl)} → RL ${goalTarget} — click to view or update`
      : `${formatLevel("RL", rl)} — click to view or update`;
    return (
      <td className={tdClass} style={{ position: "relative" }}>
        <button
          type="button"
          className={`mcell assessed${highlightCls}${pendingCls}`}
          style={{
            background: "var(--gray-50)",
            border: "none",
            width: "100%",
            cursor: "pointer",
          }}
          title={hoverTitle || title}
          onClick={() => onAssess(assessPayload)}
          {...cellDataAttrs}
        >
          <div
            className={`rl-dot${goalRingCls}`}
            style={{ background: dotColor }}
            aria-hidden
          />
          <div className="rl-dot-label">
            {formatLevel("RL", rl)}
            {goalTarget ? (
              <span className="goal-target">
                <span className="goal-arrow">→</span>
                {goalTarget}
              </span>
            ) : null}
          </div>
          {pair.pendingConfirm ? (
            <span
              className="mcell-review-badge"
              title="Inherited RL — review pending"
            >
              Review
            </span>
          ) : null}
        </button>
        {showSavedToast ? <CellSavedToast /> : null}
      </td>
    );
  }

  const pendingCls = pair.pendingConfirm ? " mcell-pending-review" : "";
  return (
    <td className={tdClass} style={{ position: "relative" }}>
      <button
        type="button"
        className={`mcell assessed mcell-mapped-only${highlightCls}${pendingCls}`}
        style={{
          border: "none",
          background: "var(--gray-50)",
          width: "100%",
          cursor: "pointer",
        }}
        title={hoverTitle || "Mapped — click to assess RL"}
        onClick={() => onAssess(assessPayload)}
        {...cellDataAttrs}
      >
        <div
          className="rl-dot"
          style={{ background: MAPPED_UNASSESSED_DOT_COLOR }}
          aria-hidden
        />
        {pair.pendingConfirm ? (
          <span
            className="mcell-review-badge"
            title="Inherited RL — review pending"
          >
            Review
          </span>
        ) : null}
      </button>
      {showSavedToast ? <CellSavedToast /> : null}
    </td>
  );
}

function SectionHeaderRow({
  icon: Icon,
  label,
  count,
  colSpan,
  showPsLegend = false,
  highlightColIndexes,
}) {
  const emptyCols = Array(colSpan)
    .fill(0)
    .map((_, i) => (
      <td
        key={`empty-${i}`}
        className={
          highlightColIndexes?.has(i)
            ? "section-hdr-empty mx-hl"
            : "section-hdr-empty"
        }
      />
    ));
  return (
    <tr className="section-hdr">
      <td>
        <div className="section-hdr-label">
          <Icon size={14} style={{ color: "var(--brand)", flexShrink: 0 }} />
          {label}
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              color: "var(--text-muted)",
              marginLeft: 2,
            }}
          >
            ({count})
          </span>
          {showPsLegend ? <ExecTeamPsLegend /> : null}
        </div>
      </td>
      {emptyCols}
    </tr>
  );
}

export function RelationshipsMatrixTable({
  accountId,
  accountName,
  orgName,
  contacts,
  stakeholders,
  pairs,
  departed = [],
  cellMode,
  gradeMapping,
  getGradeLabel,
  considerMeddpiccRole = true,
  onAssess,
  onUnmappedCell,
  onCoverageChanged,
  highlightedCellKeys,
  savedToastCellKey,
  highlightContactIds,
  highlightOwnerId,
}) {
  const [showContactRolesRef, setShowContactRolesRef] = useState(false);
  const columnDefs = useMemo(
    () => buildPerContactMatrixColumns(contacts, { considerMeddpiccRole }),
    [contacts, considerMeddpiccRole]
  );
  const groupSpans = useMemo(
    () => (considerMeddpiccRole ? buildMeddGroupHeaderSpans(columnDefs) : []),
    [columnDefs, considerMeddpiccRole]
  );
  const colCount = columnDefs.length;
  const dataColCount = Math.max(colCount, 1);
  const vendorColWidth = MATRIX_VENDOR_STAKEHOLDER_COL_WIDTH;
  const contactMinWidth = 100;
  const contentMinWidth = vendorColWidth + dataColCount * contactMinWidth;
  const { execTeam, accountTeam } = useMemo(
    () => splitStakeholdersForMatrix(stakeholders),
    [stakeholders]
  );
  const realContactCount = contacts.length;
  const highlightColIndexes = useMemo(() => {
    const indexes = new Set();
    if (!highlightContactIds?.size) return indexes;
    columnDefs.forEach((col, index) => {
      const id = col.contact?.id;
      if (id && highlightContactIds.has(String(id))) indexes.add(index);
    });
    return indexes;
  }, [columnDefs, highlightContactIds]);
  const ownerRowClass = function (person) {
    if (!highlightOwnerId || !person) return undefined;
    const focus = String(highlightOwnerId);
    if (
      String(person.id) === focus ||
      String(person.sourceId || "") === focus
    ) {
      return "mx-hl-row";
    }
    return undefined;
  };
  const mappedInMatrix = useMemo(() => {
    const seen = new Set();
    for (const stakeholder of stakeholders || []) {
      for (const col of columnDefs) {
        const { pair } = findPairForMatrixCell(pairs, stakeholder.id, col);
        if (pair) seen.add(pair.id);
      }
    }
    return seen.size;
  }, [stakeholders, columnDefs, pairs]);

  const renderDataCells = stakeholder => {
    const cells = columnDefs.map(col => {
      if (col.isGhost)
        return <React.Fragment key={col.key}>{GHOST_CELL}</React.Fragment>;
      const { pair, contact } = findPairForMatrixCell(
        pairs,
        stakeholder.id,
        col
      );
      const cellKey = contact ? `${stakeholder.id}:${contact.id}` : null;
      const isHighlighted = Boolean(
        cellKey && highlightedCellKeys?.has(cellKey)
      );
      const showSavedToast = Boolean(
        cellKey && savedToastCellKey && cellKey === savedToastCellKey
      );
      const coverageColumn = Boolean(
        contact?.id && highlightContactIds?.has(String(contact.id))
      );
      return (
        <MatrixCell
          key={`${stakeholder.id}-${col.key}`}
          stakeholder={stakeholder}
          columnDef={col}
          pair={pair}
          activeContact={contact}
          cellMode={cellMode}
          onAssess={onAssess}
          onUnmappedCell={onUnmappedCell}
          isHighlighted={isHighlighted}
          showSavedToast={showSavedToast}
          cellKey={cellKey}
          coverageColumn={coverageColumn}
        />
      );
    });
    if (colCount === 0) {
      cells.push(
        <td key="matrix-fill" className="matrix-fill-col" aria-hidden />
      );
    }
    return cells;
  };

  return (
    <>
      {pairs.length > 0 && mappedInMatrix < pairs.length ? (
        <div
          className="coverage-prompt"
          style={{ margin: "0 0 8px", borderRadius: 6 }}
          role="status"
        >
          <span>
            <strong>{pairs.length}</strong> mapped pair
            {pairs.length === 1 ? "" : "s"} saved ·{" "}
            <strong>{mappedInMatrix}</strong> visible on matrix
            {mappedInMatrix < pairs.length
              ? " (re-save mapping on Account Detail or refresh)"
              : ""}
          </span>
        </div>
      ) : null}
      {considerMeddpiccRole && showContactRolesRef ? (
        <ContactRolesReferenceCard />
      ) : null}
      <div className="matrix-scroll-inner">
        <table
          id="matrix-table"
          className="matrix-tbl matrix-tbl--fill"
          style={{
            width: "100%",
            minWidth: contentMinWidth,
            "--matrix-vendor-col-width": `${vendorColWidth}px`,
          }}
        >
          <colgroup>
            <col
              className="matrix-col-vendor"
              style={{ width: vendorColWidth }}
            />
            {columnDefs.map(col => (
              <col key={col.key} className="matrix-col-contact" />
            ))}
            {colCount === 0 ? <col className="matrix-col-fill" /> : null}
          </colgroup>
          <thead>
            <tr>
              <th
                className="rh"
                rowSpan={3}
                style={{
                  borderBottom: "1px solid var(--border)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <svg
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                  }}
                  preserveAspectRatio="none"
                >
                  <line
                    x1="0"
                    y1="0"
                    x2="100%"
                    y2="100%"
                    stroke="#e5e7eb"
                    strokeWidth="1"
                  />
                </svg>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 7,
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--gray-600)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    textAlign: "right",
                    lineHeight: 1.3,
                  }}
                >
                  {accountName}
                  <br />
                  <span
                    style={{
                      fontSize: 8.5,
                      fontWeight: 500,
                      color: "var(--text-muted)",
                    }}
                  >
                    Contacts
                  </span>
                </div>
                <div
                  style={{
                    position: "absolute",
                    bottom: 10,
                    left: 7,
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--gray-600)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    lineHeight: 1.3,
                  }}
                >
                  {orgName || "Vendor"}
                  <br />
                  <span
                    style={{
                      fontSize: 8.5,
                      fontWeight: 500,
                      color: "var(--text-muted)",
                    }}
                  >
                    Team
                  </span>
                </div>
              </th>
              <th colSpan={dataColCount} className="contacts-section-hdr">
                <div className="contacts-section-hdr-inner">
                  <Users
                    size={13}
                    style={{ flexShrink: 0, color: "var(--gray-500)" }}
                  />
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "var(--gray-600)",
                    }}
                  >
                    Account Contacts
                  </span>
                  <span style={{ fontSize: 10, color: "var(--text-muted)" }}>
                    ({realContactCount})
                  </span>
                  {considerMeddpiccRole ? (
                    <button
                      type="button"
                      className="medd-ref-toggle"
                      onClick={() => setShowContactRolesRef(open => !open)}
                      aria-expanded={showContactRolesRef}
                      aria-label={
                        showContactRolesRef
                          ? "Hide contact roles reference"
                          : "Show contact roles reference"
                      }
                    >
                      {showContactRolesRef
                        ? "Contact Roles ×"
                        : "Contact Roles ?"}
                    </button>
                  ) : null}
                </div>
              </th>
            </tr>
            {groupSpans.length > 0 ? (
              <tr>
                {groupSpans.map(grp => {
                  const displayLabel = formatMeddGroupLabelDisplay(grp.label);
                  return (
                    <th
                      key={grp.group}
                      className={`medd-grp-hdr medd-grp-${grp.group}`}
                      colSpan={grp.span}
                      title={displayLabel}
                    >
                      {displayLabel}
                    </th>
                  );
                })}
              </tr>
            ) : colCount === 0 ? (
              <tr>
                <th
                  colSpan={dataColCount}
                  className="contacts-section-hdr contacts-section-hdr--empty"
                >
                  <span
                    style={{
                      fontSize: 10,
                      color: "var(--text-muted)",
                      fontStyle: "italic",
                    }}
                  >
                    No account contacts yet — add contacts to build the matrix
                  </span>
                </th>
              </tr>
            ) : null}
            <tr>
              {columnDefs.map(col => {
                const meddKey = col.meddKey || col.key;
                const displayMeddKey = formatMeddKeyDisplay(meddKey);
                const displayMeddLabel = formatMeddRoleLabelDisplay(col.label);
                const mCls = `medd medd-${String(meddKey).toLowerCase()}`;
                const avatarBg = getCustomerContactAvatarBg();
                if (col.isGhost) {
                  return (
                    <th
                      key={col.key}
                      className={`c-col mcol-ghost mcol-${col.group}`}
                      title={displayMeddLabel}
                    >
                      <div
                        className="c-avatar"
                        style={{ background: avatarBg, opacity: 0.3 }}
                      >
                        ?
                      </div>
                      <div
                        className="c-name"
                        style={{ color: "var(--gray-400)" }}
                      >
                        —
                      </div>
                      <span
                        className={mCls}
                        style={{
                          marginTop: 2,
                          display: "inline-flex",
                          fontSize: 9,
                          opacity: 0.5,
                        }}
                      >
                        {displayMeddKey}
                      </span>
                    </th>
                  );
                }
                const c = col.contact;
                const memberTitle = resolveCustomerMemberTitle(c);
                return (
                  <th
                    key={col.key}
                    data-contact-col={c?.id ? String(c.id) : undefined}
                    className={`c-col mcol-${col.group}${
                      c?.id && highlightContactIds?.has(String(c.id))
                        ? " mx-hl"
                        : ""
                    }`}
                    title={`${c.name}${memberTitle ? ` · ${memberTitle}` : ""}${considerMeddpiccRole && col.meddKey ? ` · ${displayMeddLabel}` : ""}`}
                  >
                    <div className="c-avatar" style={{ background: avatarBg }}>
                      {getInitials(c.name)}
                    </div>
                    <div className="c-name">{c.name}</div>
                    {memberTitle ? (
                      <div className="c-title">{memberTitle}</div>
                    ) : null}
                    {considerMeddpiccRole && col.meddKey ? (
                      <span
                        className={mCls}
                        style={{
                          marginTop: 2,
                          display: "inline-flex",
                          fontSize: 9,
                        }}
                        title={displayMeddLabel}
                      >
                        {displayMeddKey}
                      </span>
                    ) : null}
                  </th>
                );
              })}
              {colCount === 0 ? (
                <th className="matrix-fill-col" aria-hidden />
              ) : null}
            </tr>
          </thead>
          <tbody>
            <SectionHeaderRow
              icon={Users}
              label="Exec Team"
              count={execTeam.length}
              colSpan={dataColCount}
              showPsLegend
              highlightColIndexes={highlightColIndexes}
            />
            {execTeam.map(stakeholder => (
              <tr
                key={stakeholder.id}
                data-matrix-owner={String(stakeholder.id)}
                className={ownerRowClass(stakeholder)}
              >
                <td style={{ position: "relative" }}>
                  <VendorRowLabel vendor={stakeholder} variant="matrix" />
                </td>
                {renderDataCells(stakeholder)}
              </tr>
            ))}
            {accountTeam.length > 0 ? (
              <>
                <SectionHeaderRow
                  icon={Briefcase}
                  label="Account Team"
                  count={accountTeam.length}
                  colSpan={dataColCount}
                  highlightColIndexes={highlightColIndexes}
                />
                {accountTeam.map(stakeholder => (
                  <tr
                    key={stakeholder.id}
                    data-matrix-owner={String(stakeholder.id)}
                    className={ownerRowClass(stakeholder)}
                  >
                    <td style={{ position: "relative" }}>
                      <VendorRowLabel vendor={stakeholder} variant="matrix" />
                    </td>
                    {renderDataCells(stakeholder)}
                  </tr>
                ))}
              </>
            ) : null}
            <DepartedRowSection
              accountId={accountId}
              departed={departed}
              contacts={contacts}
              columnDefs={columnDefs}
              onCoverageChanged={onCoverageChanged}
              highlightContactIds={highlightContactIds}
              highlightOwnerId={highlightOwnerId}
              highlightColIndexes={highlightColIndexes}
            />
          </tbody>
        </table>
      </div>
    </>
  );
}

export default RelationshipsMatrixTable;
